import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    loadDocumentTypes,
    loadJobSectorDetails,
    loadParticipantStatusDetails,
    loadPoolAcceptedRejectionReasonsDetails,
    loadPoolRequestedRejectionReasonsDetails,
    loadPoolStatusDetails
} from '../../../../store/directusService';
import {
    downloadParticipantDocumentById,
    loadParticipantDocumentDetails
} from '../../../../store/documentService';
import { loadParticipantEmployability } from '../../../../store/participantService';
import {
    loadPoolApplicationsByParticipant,
    loadPoolsForApplications
} from '../../../../store/recruitmentService';
import { getNameFromId } from '../../../../utils/directusFunctions';
import { DEFAULT_TABLE_ROWS_PER_PAGE } from '../../../../utils/formValidation/commonConstants';
import {
    DOCUMENT_TYPES_ERROR,
    JOB_SECTOR_DETAILS_ERROR,
    PARTICIPANT_STATUS_DETAILS_ERROR,
    POOL_ACCEPTED_REJECTION_REASONS_DETAILS_ERROR,
    POOL_REQUESTED_REJECTION_REASONS_DETAILS_ERROR,
    POOL_STATUS_DETAILS_ERROR
} from '../../../../utils/formValidation/loadingErrorMessageConstants';
import { stableSort } from '../../../../utils/sortFunctions';
import {
    ADVISER,
    hasRole,
    MANAGER,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../../utils/userRoles';
import Button from '../../../formElements/Button';
import DynamicSearch from '../../../search/DynamicSearch';
import ResultsTable from '../../../table/ResultsTable';
import LoadingSpinner from '../../../ui/LoadingSpinner';

import ApplyToPoolEditor from './selectPools/ApplyToPoolEditor';
import PoolsRow from './PoolsRow';

import app from '../../../../app.module.css';
import classes from '../submissionStyles/pools.module.css';

const headCells = [
    { id: 'code', numeric: false, label: 'ID' },
    { id: 'ownerName', numeric: false, label: 'Pool Owner' },
    { id: 'sectorName', numeric: false, label: 'Job Sector' },
    { id: 'cv', numeric: false, label: 'CV' },
    { id: 'downloadCV', numeric: false, label: 'Download CV', sortable: false },
    { id: 'startDate', numeric: false, label: 'Pool Start Date' },
    { id: 'statusName', numeric: false, label: 'Status' }
];

const initialRowMetaData = {
    order: 'asc',
    orderBy: 'code',
    page: 0,
    rowsPerPage: DEFAULT_TABLE_ROWS_PER_PAGE
};

const acceptedRoles = [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];

const Pools = () => {
    //HOOKS
    const dispatch = useDispatch();

    // LOCAL STATE
    const participantClosedStatusRoles = [MANAGER, QUALITY, SUPERUSER];
    const [participantDocumentsAddName, setParticipantDocumentsAddName] = useState([]);
    const [participantPools, setParticipantPools] = useState([]);
    const [rows, setRows] = useState([]);
    const [filteredRows, setFilteredRows] = useState([]);
    const [poolApplicationsLoaded, setPoolApplicationsLoaded] = useState(false);
    const [storedPools, setStoredPools] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isOpenPoolEditor, setIsOpenPoolEditor] = useState(false);
    const [openId, setOpenId] = useState('');
    const [rowMetaData, setRowMetaData] = useState(initialRowMetaData);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const users = useSelector((state) => state.entities.userService.usersByLoggedInUserServiceIds);
    const documentTypes = useSelector((state) => state.entities.directusService.documentTypes);
    const jobSectorDetails = useSelector(
        (state) => state.entities.directusService.jobSectorDetails
    );
    const participantStatusDetails = useSelector(
        (state) => state.entities.directusService.participantStatusDetails
    );
    const poolAcceptedRejectionReasonsDetails = useSelector(
        (state) => state.entities.directusService.poolAcceptedRejectionReasonsDetails
    );
    const poolRequestedRejectionReasonsDetails = useSelector(
        (state) => state.entities.directusService.poolRequestedRejectionReasonsDetails
    );
    const poolStatusDetails = useSelector(
        (state) => state.entities.directusService.poolStatusDetails
    );
    const participantDocuments = useSelector(
        (state) => state.entities.documentService.participantDocumentDetails
    );
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const poolApplications = useSelector(
        (state) => state.entities.recruitmentService.poolApplications
    );
    const pools = useSelector((state) => state.entities.recruitmentService.pools);
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        documentTypes?.length < 1 && dispatch(loadDocumentTypes());
        jobSectorDetails?.length < 1 && dispatch(loadJobSectorDetails());
        participantStatusDetails?.length < 1 && dispatch(loadParticipantStatusDetails());
        poolAcceptedRejectionReasonsDetails?.length < 1 &&
            dispatch(loadPoolAcceptedRejectionReasonsDetails());
        poolRequestedRejectionReasonsDetails?.length < 1 &&
            dispatch(loadPoolRequestedRejectionReasonsDetails());
        poolStatusDetails?.length < 1 && dispatch(loadPoolStatusDetails());
        dispatch(loadParticipantDocumentDetails(currentParticipant.id));
        dispatch(loadParticipantEmployability(currentParticipant.id));
        dispatch(loadPoolApplicationsByParticipant(currentParticipant.id));
    }, []);

    useEffect(() => {
        if (poolApplications.length < 1) return;
        setStoredPools([]);
        const poolIds = poolApplications.map((el) => el.participantPoolId);
        dispatch(loadPoolsForApplications(poolIds));
    }, [poolApplications]);

    useEffect(() => {
        if (poolApplications.length < 1 || pools.length < 1 || storedPools.length > 0) return;

        setStoredPools(pools);
    }, [pools]);

    useEffect(() => {
        if (storedPools.length < 1) return;

        let updatedPoolApplications = poolApplications;

        storedPools.forEach((el) => {
            const user = users.find((entry) => entry.id === el?.ownerId);
            updatedPoolApplications = updatedPoolApplications.map((entry) => {
                if (entry.participantPoolId === el.id) {
                    return {
                        ...entry,
                        code: el.code,
                        ownerId: el.ownerId,
                        ownerName: `${user?.firstName || ''} ${user?.lastName || ''}`,
                        sectorId: el.jobSectorId
                    };
                } else {
                    return entry;
                }
            });
        });

        setParticipantPools(updatedPoolApplications);
        setRows(updatedPoolApplications);
    }, [storedPools]);

    useEffect(() => {
        if (participantDocuments?.length === 0) {
            setParticipantDocumentsAddName([]);
            return;
        }

        if (
            !participantDocuments?.length ||
            documentTypes?.length < 1 ||
            (participantPools?.length < 1 && !poolApplicationsLoaded)
        )
            return;

        const goldStandardCVDocumentTypeId = documentTypes.find(
            (entry) => entry.name.toLowerCase() === 'gold standard cv'
        )?.id;
        const updated = participantDocuments
            .filter((el) => el.typeId === goldStandardCVDocumentTypeId)
            .map((el) => ({ ...el, name: el.filename }));
        setParticipantDocumentsAddName(updated);

        const updatedPoolApplications = participantPools.map((el) => ({
            ...el,
            sectorName: getNameFromId(jobSectorDetails, el.sectorId),
            filename: getDocumentName(el.goldStandardCVId),
            documentId: el.goldStandardCVId,
            statusName: getNameFromId(poolStatusDetails, el.statusId)
        }));

        setRows(updatedPoolApplications);
        setFilteredRows(updatedPoolApplications);
    }, [participantDocuments, documentTypes, poolApplicationsLoaded, participantPools]);

    useEffect(() => {
        if (!rows.length) return;

        const filtered = !searchTerm
            ? rows
            : rows.filter((el) => el.sectorName.toLowerCase().includes(searchTerm.toLowerCase()));
        setFilteredRows(filtered);
    }, [searchTerm]);

    useEffect(() => {
        if (successMessage === `Pool applications have been loaded`) {
            setPoolApplicationsLoaded(true);
        }
    }, [successMessage]);

    // HELPER FNS
    const createRows = () =>
        stableSort(rows, rowMetaData.orderBy, rowMetaData.order).map((el) => (
            <PoolsRow
                key={el.id}
                row={el}
                roles={roles}
                acceptedRoles={acceptedRoles}
                openId={openId}
                toggleDropdown={onToggleDropdown}
                onDownload={onDownload}
            />
        ));
    const getDocumentName = (id) => {
        if (!id || id === '') {
            return '';
        }
        return participantDocuments.find((el) => el.id === id)?.filename || '';
    };

    const loadingError = () => {
        if (documentTypes?.length < 1) return DOCUMENT_TYPES_ERROR;
        if (jobSectorDetails?.length < 1) return JOB_SECTOR_DETAILS_ERROR;
        if (poolAcceptedRejectionReasonsDetails?.length < 1)
            return POOL_ACCEPTED_REJECTION_REASONS_DETAILS_ERROR;
        if (poolRequestedRejectionReasonsDetails?.length < 1)
            return POOL_REQUESTED_REJECTION_REASONS_DETAILS_ERROR;
        if (poolStatusDetails?.length < 1) return POOL_STATUS_DETAILS_ERROR;
        if (participantStatusDetails?.length < 1) return PARTICIPANT_STATUS_DETAILS_ERROR;
    };

    // EVENT HANDLERS
    const onSearch = (searchRes) => setSearchTerm(searchRes);

    // EVENT HANDLER CALLBACKS
    const onToggleDropdown = (id) => setOpenId(id);

    const onDownload = (row) => {
        dispatch(
            downloadParticipantDocumentById(currentParticipant.id, row.documentId, row.filename)
        );
    };

    // AWAITING CONTENT
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} time={3} />;

    // RENDER
    return (
        <>
            <h2 className={app.sectionHeading}>Participant Pooling</h2>
            <div className={classes.poolToolBar} data-testid="pool_form">
                <DynamicSearch
                    search={onSearch}
                    searchTerm={searchTerm}
                    placeholder="Enter Search Term"
                    disabled={!hasRole(acceptedRoles, roles)}
                />
                <div>
                    <Button
                        id="ApplyToPoolButton"
                        type="button"
                        customClass="poolsButton"
                        disabled={
                            !hasRole(acceptedRoles, roles) ||
                            (getNameFromId(
                                participantStatusDetails,
                                currentParticipant.statusId
                            ) === 'Closed' &&
                                !hasRole(participantClosedStatusRoles, roles))
                        }
                        content="Apply to new Pool"
                        icon={true}
                        onClick={() => setIsOpenPoolEditor(true)}
                    />
                    {isOpenPoolEditor && (
                        <ApplyToPoolEditor
                            isOpen={isOpenPoolEditor}
                            onClosePoolEditor={() => setIsOpenPoolEditor(false)}
                            participantDocuments={participantDocumentsAddName}
                            poolApplications={poolApplications}
                        />
                    )}
                </div>
            </div>

            {filteredRows.length < 1 ? (
                <div>No participant pools found</div>
            ) : (
                <ResultsTable
                    defaultOrderBy={rowMetaData.orderBy}
                    sortOrder={rowMetaData.order}
                    headCells={headCells}
                    passRowMetaDataUp={setRowMetaData}
                    tableRows={createRows()}
                    totalResults={rows.length}
                />
            )}
        </>
    );
};

export default Pools;
